import { useState, useEffect, JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useCardTransactionsStore } from '@lib/store';
import { useAccountContext, useResponsive } from '@shared/hooks';
import { FormatUtils, PaletteUtils } from '@shared/utils';
import type { CardTransactions } from '@shared/api';
import { DescriptionButton, MobileItemView, TooltipCell, TransactionsHeader } from './widgets';
import { MobileHeader } from '@widgets';
import { DatePicker, Empty, SelectedDays, Spinner, Table } from '@shared/widgets';
import TransactionIcon from '@assets/icons/cards/transactions.svg';
import ArrowIcon from '@assets/icons/cards/arrow.svg';

const COUNT_PER_PAGE = 20;

const filterTransactionsByDate = (transactions: CardTransactions[], dateFrom?: string, dateTo?: string) => {
  if (!dateFrom || !dateTo) return transactions;

  const startDate = dayjs(dateFrom).startOf('day');
  const endDate = dayjs(dateTo).endOf('day');

  return transactions.filter((transaction) => {
    const transactionDate = dayjs(transaction.effectiveDate);
    return transactionDate.isBetween(startDate, endDate, null, '[]');
  });
};

export interface Column {
  dataIndex: keyof CardTransactions | string;
  title?: string;
  mobile?: boolean;
  width?: string | number;
  className?: string;
  render?: (value: any, record: { record: CardTransactions }) => JSX.Element | string | null;
  onCell?: () => { style: React.CSSProperties };
}

export interface TransactionsState {
  index: number;
  dateFrom?: string;
  dateTo?: string;
}
const INITIAL_STATE: TransactionsState = { index: 0, dateFrom: '', dateTo: '' };

export const Transactions = () => {
  const cardTransactionsStore = useCardTransactionsStore();

  const [state, setState] = useState<TransactionsState>(INITIAL_STATE);
  const [popup, setPopup] = useState<{ record: CardTransactions; card: any } | null>(null);

  const { account, card } = useAccountContext();
  const { isMobile } = useResponsive();
  const { t } = useTranslation('card');

  useEffect(() => {
    if (!account?.id || !card?.id) return;
    setState(INITIAL_STATE);
    cardTransactionsStore.clear();
    cardTransactionsStore.getCardTransactions({ accountId: account.id, cardId: card.id, index: 0, count: COUNT_PER_PAGE });
  }, [account?.id, card?.id]);

  const handleNextPage = (index: number) => {
    setState((prev) => ({ ...prev, index }));
    if (!account || !card) return;
    cardTransactionsStore.getCardTransactions({ accountId: account?.id, cardId: card?.id, index, count: COUNT_PER_PAGE });
  };

  const handleChangeRangeDay = (range: SelectedDays | null) => {
    setState(() => {
      if (range)
        return { dateFrom: dayjs(range.from).toISOString().substring(0, 10), dateTo: dayjs(range.to).toISOString().substring(0, 10), index: 0 };
      return INITIAL_STATE;
    });
    if (!account || !card) return;
    cardTransactionsStore.getCardTransactions({ accountId: account?.id, cardId: card?.id, index: 0, count: COUNT_PER_PAGE });
  };

  const columns: Column[] = [
    {
      dataIndex: 'descriptionDate',
      className: '!pl-5 max-[840px]:pr-5 pr-0',
      mobile: true,
      render: (_, { record }) => (
        <>
          {dayjs(record.effectiveDate).format('DD.MM.YYYY')}
          <br />
          <span className='uppercase text-[#6b6b70]'>{record.description}</span>
        </>
      ),
    },
    {
      dataIndex: 'transactionId',
      className: 'px-2 text-center',
      width: '108px',
      title: t('transactions.columns.ref'),
      render: (value, { record }) => (value !== 0 ? value : record.status),
    },
    {
      dataIndex: 'date',
      className: 'pr-2',
      width: '145px',
      title: t('transactions.columns.date'),
      render: (_, { record }) => dayjs(record.effectiveDate).format('DD.MM.YYYY'),
    },
    {
      dataIndex: 'description',
      className: PaletteUtils.isBordierPalette ? 'text-base font-semibold' : 'text-sm',
      title: t('transactions.columns.description'),
      render: (value, { record }) => (
        <TooltipCell>
          {record.amountSigned && record.amountSigned < 0 ? (
            <span className='text-[#dc291c]'>{t('transactions.columns.credit').toUpperCase()}: </span>
          ) : null}
          {value}
        </TooltipCell>
      ),
    },
    {
      dataIndex: 'location',
      width: '120px',
      title: t('transactions.columns.location'),
      render: (_, { record }) => (record.location ? record.location : '-'),
    },
    {
      dataIndex: 'amount',
      className: clsx('pr-3', PaletteUtils.isBordierPalette ? 'text-base font-semibold' : 'text-sm', isMobile && 'border-l-0 pl-0 pr-1 text-right'),
      width: isMobile ? '90px' : '167px',
      mobile: isMobile,
      title: t('transactions.columns.amountIn', { currency: account?.currency }),
      render: (value, { record }) => (
        <div className='flex justify-between'>
          {value ? FormatUtils.formatAmount(record.totalAmount, account?.currency) : null}
          {record.feesDescription ? <DescriptionButton record={record} /> : undefined}
        </div>
      ),
    },
    {
      dataIndex: 'amount',
      className: clsx('pr-3 text-sm', PaletteUtils.isBordierPalette && 'font-bold'),
      width: '182px',
      title: t('transactions.columns.localCurrency'),
      render: (value, { record }) => <>{value ? FormatUtils.formatAmount(record.originalAmount, record.originalCurrency) : null}</>,
    },
    {
      dataIndex: 'arrowMove',
      className: 'w-8',
      width: 32,
      mobile: true,
      render: () => <ArrowIcon className={clsx(PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#BE8B45]')} />,
      onCell: () => ({ style: { padding: 0, position: 'relative' } }),
    },
  ];

  const filteredTransactions = useMemo(
    () => filterTransactionsByDate(cardTransactionsStore.data?.data || [], state.dateFrom, state.dateTo),
    [cardTransactionsStore.data?.data, state.dateFrom, state.dateTo],
  );

  if (!account || !card) return null;
  return (
    <>
      <TransactionsHeader state={state} />
      <MobileHeader className='gap-4' contentClassName='max-[840px]:w-15'>
        <div className='ml-auto -mt-10 max-[840px]:-mt-1'>
          <DatePicker isMobile={isMobile} range={{ from: state.dateFrom, to: state.dateTo }} onConfirm={handleChangeRangeDay} />
        </div>
      </MobileHeader>
      <div>
        {cardTransactionsStore.loading && <Spinner className='min-h-[200px]' />}
        <Table
          showPaginate={cardTransactionsStore.hasNext}
          emptyText={<Empty icon={<TransactionIcon />} title={t('transactions.empty')} />}
          loading={cardTransactionsStore.loading}
          subLoading={cardTransactionsStore.subLoading}
          subLoadingClass='max-[840px]:min-h-0'
          onRow={(record: CardTransactions) => ({ onClick: () => (isMobile ? setPopup({ record, card }) : undefined) })}
          onNextPage={handleNextPage}
          from={state.index}
          count={COUNT_PER_PAGE}
          isMobile={isMobile}
          columns={columns}
          data={filteredTransactions}
        />
      </div>
      {popup && <MobileItemView onClose={() => setPopup(null)} transaction={popup.record} account={account} />}
    </>
  );
};
